import { ButtonSecondary, Card, Colors, Heading, Icon, Pills, Text } from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';
import type { IconName } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

export type EvaluationClosedVariant =
    | 'closed'
    | 'alreadyAnswered'
    | 'unavailable'
    | 'respondentRemoved'
    | 'answerLoadError'
    | 'commentThreadNotFound'
    | 'commentThreadOfAnotherPerson'
    | 'commentThreadLoadError';

const COPY: Record<
    EvaluationClosedVariant,
    { pill: TranslationKey; title: TranslationKey; description: TranslationKey; icon?: IconName }
> = {
    closed: {
        pill: 'reviewAnswer.evaluationClosed.pill',
        title: 'reviewAnswer.evaluationClosed.title',
        description: 'reviewAnswer.evaluationClosed.description',
    },
    alreadyAnswered: {
        pill: 'reviewAnswer.alreadyAnswered.pill',
        title: 'reviewAnswer.alreadyAnswered.title',
        description: 'reviewAnswer.alreadyAnswered.description',
    },
    unavailable: {
        pill: 'reviewAnswer.unavailable.pill',
        title: 'reviewAnswer.unavailable.title',
        description: 'reviewAnswer.unavailable.description',
    },
    respondentRemoved: {
        pill: 'reviewAnswer.unavailable.pill',
        title: 'reviewAnswer.unavailable.title',
        description: 'reviewAnswer.unavailable.respondentRemoved',
    },
    answerLoadError: {
        pill: 'reviewAnswer.answerLoadError.pill',
        title: 'reviewAnswer.answerLoadError.title',
        description: 'reviewAnswer.answerLoadError.description',
        icon: 'essential-danger',
    },
    commentThreadNotFound: {
        pill: 'reviewAnswer.commentThread.notFound.pill',
        title: 'reviewAnswer.commentThread.notFound.title',
        description: 'reviewAnswer.commentThread.notFound.description',
    },
    commentThreadOfAnotherPerson: {
        pill: 'reviewAnswer.commentThread.anotherPerson.pill',
        title: 'reviewAnswer.commentThread.anotherPerson.title',
        description: 'reviewAnswer.commentThread.anotherPerson.description',
    },
    commentThreadLoadError: {
        pill: 'reviewAnswer.commentThread.unavailable.pill',
        title: 'reviewAnswer.commentThread.loadErrorTitle',
        description: 'reviewAnswer.commentThread.loadError',
    },
};

interface EvaluationClosedPageProps {
    variant?: EvaluationClosedVariant;
    /** What the reader can do from here: open the replies to their comments, or try again. */
    action?: { label: string; onClick: () => void };
}

const EvaluationClosedPage = ({ variant = 'closed', action }: EvaluationClosedPageProps) => {
    const { t } = useAmelioTranslations();
    const copy = COPY[variant];

    return (
        <div
            className={
                'relative flex min-h-screen w-full items-center justify-center overflow-hidden px-6'
            }
        >
            <Card className={'bg-primary-50 relative w-full max-w-lg rounded-2xl p-8 px-8'}>
                <div className={'absolute -top-3 -left-7'}>
                    <Pills
                        variant={'purple'}
                        size={'sm'}
                    >
                        {t(copy.pill)}
                    </Pills>
                </div>

                <div className={'flex items-center gap-6'}>
                    <div
                        className={
                            'bg-primary-50 flex aspect-square h-16 w-16 shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={copy.icon ?? 'security-lock-1'}
                            size={32}
                            color={Colors.PRIMARY}
                            aria-hidden
                        />
                    </div>

                    <div>
                        <Heading level={4}>{t(copy.title)}</Heading>

                        <Text
                            size={'small'}
                            className={'mt-1'}
                            color={Colors.BASE_600}
                        >
                            {t(copy.description)}
                        </Text>

                        {action && (
                            <ButtonSecondary
                                size={'sm'}
                                fitContent
                                // Beside the lock, a phone leaves the label less than its width on one line.
                                wrap
                                className={'mt-4'}
                                onClick={action.onClick}
                            >
                                {action.label}
                            </ButtonSecondary>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default EvaluationClosedPage;
